// src/pages/UserPage/sections/OrganizationDeliveries/OrganizationDeliveries.jsx
import React, { useEffect, useState, useMemo } from 'react';
import {
  Box, Typography, CircularProgress, Alert, Checkbox,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Chip, Button, Divider, Stack, Tooltip, Tabs, Tab,
  ToggleButtonGroup, ToggleButton, IconButton,
} from '@mui/material';
import { FileText, CreditCard, CheckCircle2, Clock, Pencil, Send } from 'lucide-react';
import { InvoicePreviewModal } from './netvisor/InvoicePreviewModal';
import { InvoicesDashboard } from './dashboard/InvoicesDashboard';
import { buildNetvisorSelection } from './netvisor/invoicePreviewHelpers';
import { invoiceSelectedDeliveriesFn } from '../../../../firebase/deliveryFunctions';
import {
  collection, getDocs, orderBy, query, doc, getDoc, writeBatch,
} from 'firebase/firestore';
import { db } from '../../../../firebase/config';
import { generateOutgoingInvoices, generatePurchaseInvoices } from '../../../../utils/invoicing';
import { DeliveryEditModal } from './DeliveryEditModal';

const DAY_MS = 24 * 60 * 60 * 1000;

function fmtDate(ms) {
  if (!ms) return '—';
  return new Date(ms).toLocaleDateString('fi-FI', {
    day: 'numeric', month: 'short', year: 'numeric',
    timeZone: 'Europe/Helsinki',
  });
}

function fmtEur(n) {
  if (n == null) return '—';
  return `${Number(n).toFixed(2)} €`;
}

const INVOICE_STATUS_LABELS = {
  pending:  'Odottaa',
  invoiced: 'Laskutettu',
  paid:     'Maksettu',
};

const INVOICE_STATUS_COLORS = {
  pending:  'warning',
  invoiced: 'info',
  paid:     'success',
};

// ─── Delivery row ─────────────────────────────────────────────────────────────

function DeliveryRow({ delivery, selected, onToggle, amountKey, secondaryLabel, onEdit }) {
  const status     = delivery.invoiceStatus || 'pending';
  const hasDispute = !!delivery.reviewData && delivery?.invoiceStatus !== 'invoiced';
  const reference  = delivery.customerReference || delivery?.billing?.paymentReference || null;

  return (
    <TableRow hover selected={selected} onClick={onToggle} sx={{
      cursor: 'pointer',
      ...(hasDispute && { bgcolor: '#ff000063' }),
    }}>
      <TableCell padding="checkbox">
        <Checkbox size="small" checked={selected} onChange={onToggle} onClick={(e) => e.stopPropagation()} />
      </TableCell>
      <TableCell>
        <Typography variant="caption" fontFamily="monospace">
          {delivery.deliveryId || delivery.id}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="caption">{fmtDate(delivery.completedAtMs)}</Typography>
      </TableCell>
      <TableCell>
        <Tooltip title={delivery.origin || ''}>
          <Typography variant="caption" sx={{ maxWidth: 110, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {delivery.origin || '—'}
          </Typography>
          <Typography variant="caption" sx={{ maxWidth: 110, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {delivery.destination || '—'}
          </Typography>
        </Tooltip>
      </TableCell>
      <TableCell>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {secondaryLabel || '—'}
        </Typography>
        {reference && (
          <Typography variant="caption" color="text.secondary"
            sx={{ display: 'block', fontFamily: 'monospace', opacity: 0.7 }}>
            Viite: {reference}
          </Typography>
        )}
      </TableCell>
      <TableCell>
        <Typography variant="caption" fontWeight={600}>{fmtEur(delivery[amountKey])}</Typography>
      </TableCell>
      <TableCell>
        <Chip
          label={INVOICE_STATUS_LABELS[status] || status}
          color={INVOICE_STATUS_COLORS[status] || 'default'}
          size="small"
          icon={status === 'paid' ? <CheckCircle2 size={12} /> : <Clock size={12} />}
          sx={{ height: 22, fontSize: '0.7rem' }}
        />
      </TableCell>
      <TableCell padding="none" align="center" onClick={(e) => e.stopPropagation()}>
        <Tooltip title="Muokkaa" arrow>
          <IconButton size="small" onClick={() => onEdit(delivery)} sx={{ p: 0.75 }}>
            <Pencil size={13} opacity={0.5} />
          </IconButton>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
}

// ─── Format toggle ────────────────────────────────────────────────────────────

function FormatToggle({ value, onChange, options = ['pdf', 'finvoice'] }) {
  const LABELS = { pdf: 'PDF', finvoice: 'XML', netvisor: 'Netvisor' };
  return (
    <ToggleButtonGroup
      value={value} exclusive
      onChange={(_, v) => { if (v) onChange(v); }}
      size="small"
      sx={{
        '& .MuiToggleButton-root': {
          px: 1.5, py: 0.5,
          fontSize: '0.7rem', fontWeight: 600,
          textTransform: 'none', borderColor: 'divider', color: 'text.secondary',
          '&.Mui-selected': { color: 'primary.main', bgcolor: 'action.selected' },
        },
      }}
    >
      {options.map((o) => (
        <ToggleButton key={o} value={o}>{LABELS[o] || o}</ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
}

// ─── Quick date select ────────────────────────────────────────────────────────

function QuickSelect({ deliveries, onSelect, disabled }) {
  const select = (range) => {
    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    let start, end;
    if (range === 'yesterday') {
      start = todayStart - DAY_MS;
      end   = todayStart;
    } else {
      start = todayStart - 6 * DAY_MS;
      end   = todayStart + DAY_MS;
    }
    const ids = deliveries
      .filter((d) => d.completedAtMs >= start && d.completedAtMs < end)
      .map((d) => d.id);
    onSelect(new Set(ids));
  };

  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Typography variant="caption" color="text.secondary">Pikavalinta:</Typography>
      <Button size="small" variant="outlined" onClick={() => select('yesterday')} disabled={disabled}>
        Eilen
      </Button>
      <Button size="small" variant="outlined" onClick={() => select('last7')} disabled={disabled}>
        Viim. 7 pv
      </Button>
    </Stack>
  );
}

// ─── Tab panel: Laskut ────────────────────────────────────────────────────────

function InvoicesTab({ deliveries, orgData, members, onStatusUpdate, onDeliveryUpdated, organizationId }) {
  const [selected, setSelected]             = useState(new Set());
  const [generating, setGenerating]         = useState(false);
  const [sending, setSending]               = useState(false);
  const [previewOpen, setPreviewOpen]       = useState(false);
  const [error, setError]                   = useState(null);
  const [notice, setNotice]                 = useState(null);
  const [format, setFormat]                 = useState('pdf');
  const [editTarget, setEditTarget]         = useState(null);
  const [showOwnerDeliveries, setShowOwnerDeliveries] = useState(false);

  const ownerId = orgData?.ownerId;
  const ownerDeliveries   = useMemo(() => deliveries.filter((d) => d.senderId === ownerId), [deliveries, ownerId]);
  const visibleDeliveries = showOwnerDeliveries ? deliveries : deliveries.filter((d) => d.senderId !== ownerId);

  const toggle    = (id) => setSelected((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = ()   => setSelected(selected.size === visibleDeliveries.length ? new Set() : new Set(visibleDeliveries.map((d) => d.id)));

  const selectedDeliveries = visibleDeliveries.filter((d) => selected.has(d.id));
  const selectedTotal      = selectedDeliveries.reduce((s, d) => s + (Number(d.price) || 0), 0);

  const noBillingCount = selectedDeliveries.filter((d) => {
    const m = members.find((m) => m.userId === d.senderId);
    return m?.billing?.noBilling === true;
  }).length;

  const netvisor = useMemo(
    () => buildNetvisorSelection(selectedDeliveries, members),
    [selectedDeliveries, members],
  );

  const isNetvisor     = format === 'netvisor';
  const canOpenPreview = isNetvisor
    ? (netvisor.groups.length > 0 || netvisor.needsFix.length > 0)
    : selectedDeliveries.length - noBillingCount > 0;

  // For PDF/XML preview — group by sender name (no Y-number required)
  const pdfGroups = useMemo(() => {
    if (isNetvisor) return [];
    const byKey = {};
    for (const d of selectedDeliveries) {
      const member      = members.find((m) => m.userId === d.senderId);
      const noBilling   = member?.billing?.noBilling === true;
      if (noBilling) continue;
      const companyName = d.billing?.companyName || member?.billing?.companyName || member?.name || d.senderName || '—';
      const key         = d.senderId || companyName;
      if (!byKey[key]) byKey[key] = { yNumber: null, companyName, deliveries: [] };
      byKey[key].deliveries.push(d);
    }
    return Object.values(byKey).map((g) => ({
      ...g,
      subtotal: g.deliveries.reduce((s, d) => s + (Number(d.price) || 0), 0),
    }));
  }, [isNetvisor, selectedDeliveries, members]);

  const previewGroups = isNetvisor ? netvisor.groups : pdfGroups;
  const busy           = generating || sending;

  // ── PDF / Finvoice ───────────────────────────────────────────────────────────
  const handleGenerate = async () => {
    if (!selectedDeliveries.length) return;
    setGenerating(true);
    setError(null);
    try {
      generateOutgoingInvoices({
        orgInfo:    orgData.payment ?? orgData,
        deliveries: selectedDeliveries,
        senders:    members,
        format,
      });

      const batch = writeBatch(db);
      for (const d of selectedDeliveries) {
        batch.update(doc(db, 'organizations', orgData.id, 'deliveries', d.id), {
          invoiceStatus: 'invoiced',
          invoicedAtMs:  Date.now(),
        });
      }
      await batch.commit();
      onStatusUpdate(selected, 'invoiced');
      setSelected(new Set());
    } catch (err) {
      console.error(err);
      setError('Laskujen luominen epäonnistui');
    } finally {
      setGenerating(false);
    }
  };

  // ── Netvisor send ────────────────────────────────────────────────────────────
  const handleSendNetvisor = async (overrides = {}) => {
    if (!netvisor.eligible.length) return;
    setSending(true);
    setError(null);
    setNotice(null);
    try {
      const result = await invoiceSelectedDeliveriesFn(
        organizationId,
        netvisor.eligible.map((d) => d.id),
        overrides,
      );
      const { invoicesSent = 0, invoicesFailed = 0 } = result || {};

      if (invoicesFailed > 0) {
        setError(`${invoicesSent} lähetetty, ${invoicesFailed} epäonnistui — tarkista lokit`);
      } else {
        setNotice(`${invoicesSent} lasku lähetetty Netvisoriin`);
      }

      if (invoicesSent > 0) {
        onStatusUpdate(new Set(netvisor.eligible.map((d) => d.id)), 'invoiced');
        setSelected(new Set());
      }
    } catch (err) {
      console.error(err);
      setError(`Netvisor-lähetys epäonnistui: ${err.message}`);
    } finally {
      setSending(false);
      setPreviewOpen(false);
    }
  };

  const handleFixed = (id, patch) => {
    const current = deliveries.find((d) => d.id === id);
    if (current) onDeliveryUpdated({ ...current, ...patch });
  };

  return (
    <Box>
      {error  && <Alert severity="error"   sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {notice && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)}>{notice}</Alert>}

      {/* ── Dashboard ── */}
      <InvoicesDashboard
        deliveries={visibleDeliveries}
        members={members}
        organizationId={organizationId}
        hiddenDeliveries={ownerDeliveries}
        showOwnerDeliveries={showOwnerDeliveries}
        onToggleOwnerDeliveries={() => {
          setShowOwnerDeliveries((v) => !v);
          if (showOwnerDeliveries) {
            setSelected((prev) => {
              const next = new Set(prev);
              ownerDeliveries.forEach((d) => next.delete(d.id));
              return next;
            });
          }
        }}
        onStatusUpdate={onStatusUpdate}
      />

      {/* ── Top bar ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="subtitle2" fontWeight={700} color="text.secondary">
            {visibleDeliveries.length} toimitusta · laskutetaan asiakkailta
          </Typography>
          <QuickSelect
            deliveries={visibleDeliveries}
            onSelect={setSelected}
            disabled={busy}
          />
          {selected.size > 0 && (
            <Button size="small" onClick={() => setSelected(new Set())} disabled={busy}>
              Tyhjennä
            </Button>
          )}
        </Box>

        {selected.size > 0 && (
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {selected.size} valittu · {fmtEur(selectedTotal)}
              </Typography>
              {noBillingCount > 0 && (
                <Typography variant="caption" color="warning.main" sx={{ display: 'block' }}>
                  {noBillingCount} ohitetaan (ei laskutusta)
                </Typography>
              )}
              {isNetvisor && netvisor.needsFix.length > 0 && (
                <Typography variant="caption" color="warning.main" sx={{ display: 'block' }}>
                  {netvisor.needsFix.length} ilman Y-tunnusta
                </Typography>
              )}
            </Box>

            <FormatToggle
              value={format}
              onChange={setFormat}
              options={['pdf', 'finvoice', 'netvisor']}
            />

            {isNetvisor ? (
              <Tooltip
                title={
                  !canOpenPreview
                    ? 'Valituilla toimituksilla ei ole laskutettavaa Y-tunnusta'
                    : netvisor.needsFix.length > 0
                    ? `Esikatsele — ${netvisor.needsFix.length} toimitusta vaatii Y-tunnuksen täydennystä`
                    : `Esikatsele ja lähetä ${netvisor.eligible.length} toimitusta Netvisoriin`
                }
              >
                <span>
                  <Button
                    variant="contained" size="small"
                    startIcon={sending ? <CircularProgress size={14} color="inherit" /> : <Send size={15} />}
                    onClick={() => setPreviewOpen(true)}
                    disabled={busy || !canOpenPreview}
                  >
                    {sending
                      ? 'Lähetetään...'
                      : netvisor.needsFix.length > 0
                      ? `Esikatsele ja korjaa (${netvisor.needsFix.length})`
                      : `Esikatsele ja lähetä (${netvisor.eligible.length})`}
                  </Button>
                </span>
              </Tooltip>
            ) : (
              <Button
                variant="contained" size="small"
                startIcon={generating ? <CircularProgress size={14} color="inherit" /> : <FileText size={15} />}
                onClick={() => setPreviewOpen(true)}
                disabled={busy || !canOpenPreview}
              >
                {generating
                  ? 'Luodaan...'
                  : format === 'pdf' ? 'Esikatsele PDF' : 'Esikatsele XML'}
              </Button>
            )}
          </Stack>
        )}
      </Box>

      {/* ── Table ── */}
      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'action.hover' }}>
              <TableCell padding="checkbox">
                <Checkbox
                  size="small"
                  checked={deliveries.length > 0 && selected.size === deliveries.length}
                  indeterminate={selected.size > 0 && selected.size < deliveries.length}
                  onChange={toggleAll}
                />
              </TableCell>
              {['Toimitusnro', 'Pvm', 'Reitti', 'Asiakas', 'Hinta', 'Tila', ''].map((h, i) => (
                <TableCell key={i}><Typography variant="caption" fontWeight={700}>{h}</Typography></TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {deliveries.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                    Ei toimituksia
                  </Typography>
                </TableCell>
              </TableRow>
            ) : visibleDeliveries.map((d) => {
              const member    = members.find((m) => m.userId === d.senderId);
              const noBilling = member?.billing?.noBilling === true;
              const yNumber   = d.billing?.yNumber || member?.billing?.yNumber || null;
              const label = (
                <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {d?.billing?.companyName || member?.name || d.senderName || '—'}
                  {noBilling && (
                    <Chip label="Ei laskutusta" size="small" color="warning" sx={{ height: 16, fontSize: '0.6rem' }} />
                  )}
                  {!noBilling && !yNumber && (
                    <Chip label="Y-tunnus puuttuu" size="small" color="warning" variant="outlined" sx={{ height: 16, fontSize: '0.6rem' }} />
                  )}
                </Box>
              );
              return (
                <DeliveryRow
                  key={d.id} delivery={d}
                  selected={selected.has(d.id)} onToggle={() => toggle(d.id)}
                  amountKey="price" secondaryLabel={label}
                  onEdit={setEditTarget}
                />
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ── Status totals ── */}
      {visibleDeliveries.length > 0 && (
        <>
          <Divider sx={{ my: 2 }} />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 3 }}>
            {['pending', 'invoiced', 'paid'].map((status) => {
              const total = visibleDeliveries
                .filter((d) => (d.invoiceStatus || 'pending') === status)
                .reduce((s, d) => s + (Number(d.price) || 0), 0);
              if (!total) return null;
              return (
                <Box key={status} sx={{ textAlign: 'right' }}>
                  <Typography variant="caption" color="text.secondary">{INVOICE_STATUS_LABELS[status]}</Typography>
                  <Typography variant="body2" fontWeight={700}>{fmtEur(total)}</Typography>
                </Box>
              );
            })}
          </Box>
        </>
      )}

      <InvoicePreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        onConfirm={isNetvisor ? handleSendNetvisor : handleGenerate}
        confirmLabel={
          isNetvisor
            ? 'Lähetä Netvisoriin'
            : format === 'pdf' ? 'Luo PDF' : 'Luo XML'
        }
        isNetvisor={isNetvisor}
        groups={previewGroups}
        needsFix={isNetvisor ? netvisor.needsFix : []}
        members={members}
        organizationId={organizationId}
        onFixed={handleFixed}
        skippedCount={netvisor.skippedCount}
        sending={generating || sending}
        vatRate={orgData?.payment?.alvRate || orgData?.paymentInfo?.alvRate || 25.5}
      />

      <DeliveryEditModal
        open={!!editTarget}
        delivery={editTarget}
        organizationId={organizationId}
        onClose={() => setEditTarget(null)}
        onSaved={(updated) => { onDeliveryUpdated(updated); setEditTarget(null); }}
      />
    </Box>
  );
}

// ─── Tab panel: Ostolaskut ────────────────────────────────────────────────────

function PurchaseInvoicesTab({ deliveries, orgData, members, onStatusUpdate, onDeliveryUpdated, organizationId }) {
  const [selected, setSelected]     = useState(new Set());
  const [generating, setGenerating] = useState(false);
  const [error, setError]           = useState(null);
  const [format, setFormat]         = useState('pdf');
  const [editTarget, setEditTarget] = useState(null);

  const toggle    = (id) => setSelected((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = ()   => setSelected(selected.size === deliveries.length ? new Set() : new Set(deliveries.map((d) => d.id)));

  const selectedDeliveries = deliveries.filter((d) => selected.has(d.id));
  const selectedTotal      = selectedDeliveries.reduce((s, d) => s + (Number(d.reward) || 0), 0);

  const handleGenerate = async () => {
    if (!selectedDeliveries.length) return;
    setGenerating(true);
    setError(null);
    try {
      generatePurchaseInvoices({
        orgInfo:    orgData.billing ?? orgData,
        deliveries: selectedDeliveries,
        couriers:   members,
        format,
      });

      const batch = writeBatch(db);
      for (const d of selectedDeliveries) {
        batch.update(doc(db, 'organizations', orgData.id, 'deliveries', d.id), {
          purchaseStatus: 'paid',
          paidAtMs:       Date.now(),
        });
      }
      await batch.commit();
      onStatusUpdate(selected, 'paid', 'purchase');
      setSelected(new Set());
    } catch (err) {
      console.error(err);
      setError('Ostolaskujen luominen epäonnistui');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="subtitle2" fontWeight={700} color="text.secondary">
            {deliveries.length} toimitusta · maksettava kuriireille
          </Typography>
          <QuickSelect
            deliveries={deliveries}
            onSelect={setSelected}
            disabled={generating}
          />
          {selected.size > 0 && (
            <Button size="small" onClick={() => setSelected(new Set())} disabled={generating}>
              Tyhjennä
            </Button>
          )}
        </Box>

        {selected.size > 0 && (
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Typography variant="caption" color="text.secondary">
              {selected.size} valittu · {fmtEur(selectedTotal)}
            </Typography>
            <FormatToggle value={format} onChange={setFormat} />
            <Button
              variant="contained" size="small" color="secondary"
              startIcon={generating ? <CircularProgress size={14} color="inherit" /> : <CreditCard size={15} />}
              onClick={handleGenerate} disabled={generating}
            >
              {generating ? 'Luodaan...' : 'Luo ostolasku'}
            </Button>
          </Stack>
        )}
      </Box>

      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'action.hover' }}>
              <TableCell padding="checkbox">
                <Checkbox
                  size="small"
                  checked={deliveries.length > 0 && selected.size === deliveries.length}
                  indeterminate={selected.size > 0 && selected.size < deliveries.length}
                  onChange={toggleAll}
                />
              </TableCell>
              {['Toimitusnro', 'Pvm', 'Reitti', 'Kuriiri', 'Palkkio', 'Tila', ''].map((h, i) => (
                <TableCell key={i}><Typography variant="caption" fontWeight={700}>{h}</Typography></TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {deliveries.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                    Ei toimituksia
                  </Typography>
                </TableCell>
              </TableRow>
            ) : deliveries.map((d) => {
              const member  = members.find((m) => m.userId === d.courierId);
              const hasIban = !!(d.payment?.iban || member?.paymentDetails?.iban);
              return (
                <Tooltip key={d.id} title={!hasIban && member ? 'Kuriirilta puuttuu IBAN-tieto' : ''} placement="left">
                  <span style={{ display: 'contents' }}>
                    <DeliveryRow
                      delivery={{ ...d, invoiceStatus: d.purchaseStatus || 'pending' }}
                      selected={selected.has(d.id)} onToggle={() => toggle(d.id)}
                      amountKey="reward"
                      onEdit={setEditTarget}
                      secondaryLabel={
                        <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {d.courierName || '—'}
                          {!hasIban && member && (
                            <Chip label="IBAN puuttuu" size="small" color="warning" sx={{ height: 16, fontSize: '0.6rem' }} />
                          )}
                        </Box>
                      }
                    />
                  </span>
                </Tooltip>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {deliveries.length > 0 && (
        <>
          <Divider sx={{ my: 2 }} />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 3 }}>
            {['pending', 'paid'].map((status) => {
              const total = deliveries
                .filter((d) => (d.purchaseStatus || 'pending') === status)
                .reduce((s, d) => s + (Number(d.reward) || 0), 0);
              if (!total) return null;
              return (
                <Box key={status} sx={{ textAlign: 'right' }}>
                  <Typography variant="caption" color="text.secondary">
                    {status === 'paid' ? 'Maksettu' : 'Odottaa maksua'}
                  </Typography>
                  <Typography variant="body2" fontWeight={700}>{fmtEur(total)}</Typography>
                </Box>
              );
            })}
          </Box>
        </>
      )}

      <DeliveryEditModal
        open={!!editTarget}
        delivery={editTarget}
        organizationId={organizationId}
        onClose={() => setEditTarget(null)}
        onSaved={(updated) => { onDeliveryUpdated(updated); setEditTarget(null); }}
      />
    </Box>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export const OrganizationDeliveries = ({ organizationId, user, isOwner }) => {
  const [deliveries, setDeliveries] = useState([]);
  const [orgData, setOrgData]       = useState(null);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);
  const [activeTab, setActiveTab]   = useState(0);

  useEffect(() => {
    if (!organizationId) return;
    (async () => {
      try {
        setLoading(true);
        const [orgSnap, deliveriesSnap] = await Promise.all([
          getDoc(doc(db, 'organizations', organizationId)),
          getDocs(query(
            collection(db, 'organizations', organizationId, 'deliveries'),
            orderBy('completedAtMs', 'desc'),
          )),
        ]);
        if (orgSnap.exists()) setOrgData({ id: orgSnap.id, ...orgSnap.data() });
        setDeliveries(deliveriesSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.error(err);
        setError('Virhe ladattaessa tietoja');
      } finally {
        setLoading(false);
      }
    })();
  }, [organizationId]);

  const handleStatusUpdate = (ids, status, type = 'invoice') => {
    setDeliveries((prev) =>
      prev.map((d) => {
        if (!ids.has(d.id)) return d;
        return type === 'purchase'
          ? { ...d, purchaseStatus: status }
          : { ...d, invoiceStatus: status };
      })
    );
  };

  const handleDeliveryUpdated = (updated) => {
    setDeliveries((prev) => prev.map((d) => d.id === updated.id ? updated : d));
  };

  const members = orgData?.members || [];

  if (loading) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>Ladataan...</Typography>
      </Box>
    );
  }

  const tabProps = {
    orgData,
    members,
    organizationId,
    onStatusUpdate:    handleStatusUpdate,
    onDeliveryUpdated: handleDeliveryUpdated,
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}
      >
        <Tab
          icon={<FileText size={16} />} iconPosition="start"
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              Laskut
              <Chip
                label={deliveries.filter((d) => (d.invoiceStatus || 'pending') === 'pending').length}
                size="small" color="warning" sx={{ height: 18, fontSize: '0.65rem' }}
              />
            </Box>
          }
        />
        <Tab
          icon={<CreditCard size={16} />} iconPosition="start"
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              Ostolaskut
              <Chip
                label={deliveries.filter((d) => (d.purchaseStatus || 'pending') === 'pending').length}
                size="small" color="warning" sx={{ height: 18, fontSize: '0.65rem' }}
              />
            </Box>
          }
        />
      </Tabs>

      {activeTab === 0 && <InvoicesTab deliveries={deliveries} {...tabProps} />}
      {activeTab === 1 && <PurchaseInvoicesTab deliveries={deliveries} {...tabProps} />}
    </Box>
  );
};